import type { LegacyOffer, OrderMapOffer } from '../model/order-schema';

/**
 * Mode-independent core of the order "offers visualization" (cpanel `LogsInfo`
 * + `VisualizationSingleOrder3`): how an offer's outcome is coloured, dimmed and
 * filtered, and how the map is framed. No React, no i18n.
 *
 * The visualization has two data sources that disagree on field names, reason
 * numbering and what "accepted" means — see `offers-legacy.ts` (cpanel's own
 * API) and `offers-new.ts` (the Delivery service). Each adapter boils its rows
 * down to an {@link OfferRecord}; everything here works on records only.
 */

/** Which data source and business rules the visualization follows. */
export type OffersMode = 'legacy' | 'new';

export const OFFERS_MODES: readonly OffersMode[] = ['legacy', 'new'];

/** Operators land on the old behaviour until they opt in to the new one. */
export const DEFAULT_OFFERS_MODE: OffersMode = 'legacy';

export function isOffersMode(value: unknown): value is OffersMode {
  return value === 'legacy' || value === 'new';
}

/** Categorical hue of an outcome — backed by the `--offer-*` design tokens. */
export type OfferTone =
  | 'sky'
  | 'orange'
  | 'orange-soft'
  | 'orange-deep'
  | 'neutral'
  | 'pink'
  | 'teal'
  | 'blue'
  | 'blue-soft'
  | 'amber'
  | 'amber-soft'
  | 'red'
  | 'red-soft'
  | 'violet'
  | 'violet-soft'
  | 'green';

export interface OfferReason {
  /** Stable id — the legend filter key. Shared across modes when the meaning is. */
  id: string;
  /** Reason number in the owning catalog's numbering. */
  number: number;
  tone: OfferTone;
  /** i18n key under `orders:offers.` — catalogs word the same id differently. */
  labelKey: string;
}

/** Filter/legend key of offers with no (known) reason — accepted or unanswered. */
export const OFFER_NULL_REASON_ID = 'ReasonNull';
/** Offers that bounced because the courier app is off are hidden until asked for. */
export const OFFER_NOT_WORKING_REASON_ID = 'ReasonNotWorkingInt';
/** The one reason that is drawn solid even when nothing is filtered. */
export const OFFER_WRONG_EQUIPMENT_REASON_ID = 'ReasonWrongEquipmentInt';

/** A reason table: one mode's numbering of why an offer was not taken. */
export interface OfferCatalog {
  readonly mode: OffersMode;
  readonly reasons: readonly OfferReason[];
  /** Reason for a number (also accepts its string form). */
  find(value: unknown): OfferReason | undefined;
  /** Legend/filter key for a reason number. */
  filterKey(value: unknown): string;
}

export function createOfferCatalog(
  mode: OffersMode,
  reasons: readonly OfferReason[],
): OfferCatalog {
  // First entry wins, as `Array.find` did in cpanel (its table repeats numbers).
  const byNumber = new Map<number, OfferReason>();
  for (const reason of reasons) {
    if (!byNumber.has(reason.number)) byNumber.set(reason.number, reason);
  }
  const find = (value: unknown): OfferReason | undefined => {
    const n =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && value.trim() !== ''
          ? Number(value)
          : Number.NaN;
    return byNumber.get(n);
  };
  return {
    mode,
    reasons,
    find,
    filterKey: (value) => find(value)?.id ?? OFFER_NULL_REASON_ID,
  };
}

/** Legend filter state: reason id → "show only these" flag. */
export type OfferFilters = Readonly<Record<string, boolean>>;

/** A point whose parts may be missing (an order without a pickup location). */
interface LooseLatLong {
  lat?: number | null | undefined;
  long?: number | null | undefined;
}

export interface LatLong {
  lat: number;
  long: number;
}

/** What the outcome rules need to know about an offer, whatever its source. */
export interface OfferFacts {
  /** Reason number in the mode's catalog; null = none given. */
  reason: number | null;
  /** The courier took the order. */
  accepted: boolean;
  /** The offer reached the courier's screen. */
  reached: boolean;
}

interface OfferRecordBase {
  id: string | number | null;
  /** The id the live search push names the courier by (matches `useOrderOffer`). */
  courierId: string | number | null;
  /** The courier's position; null when unknown (cpanel treated 0 as "missing"). */
  coords: LatLong | null;
  /** Road distance courier → pickup, metres. */
  roadMeters: number | null;
  facts: OfferFacts;
  /** When the offer reached the courier — paint order (oldest underneath). */
  sortAt: string | null;
}

/** One offer, normalised from its source row (kept as `raw` for the detail card). */
export type OfferRecord = OfferRecordBase &
  ({ mode: 'new'; raw: OrderMapOffer } | { mode: 'legacy'; raw: LegacyOffer });

/** An offer whose courier position is known. */
export type PlottedOffer = OfferRecord & { coords: LatLong };

/** A position only when both parts are real (non-zero, finite) numbers. */
export function toLatLong(point: LooseLatLong | null | undefined): LatLong | null {
  const lat = point?.lat;
  const long = point?.long;
  return lat && long && Number.isFinite(lat) && Number.isFinite(long)
    ? { lat, long }
    : null;
}

export type OfferOutcomeKind = 'accepted' | 'reason' | 'declined' | 'undelivered';

export interface OfferOutcome {
  kind: OfferOutcomeKind;
  reason: OfferReason | undefined;
  tone: OfferTone;
}

/**
 * The outcome of one offer. Precedence mirrors cpanel: accepted wins, then a
 * known reason, then "reached the screen but not accepted", else "never
 * reached the courier".
 */
export function describeOffer(facts: OfferFacts, catalog: OfferCatalog): OfferOutcome {
  if (facts.accepted) return { kind: 'accepted', reason: undefined, tone: 'green' };
  const reason = catalog.find(facts.reason);
  if (reason) return { kind: 'reason', reason, tone: reason.tone };
  if (facts.reached) return { kind: 'declined', reason: undefined, tone: 'amber-soft' };
  return { kind: 'undelivered', reason: undefined, tone: 'red' };
}

/** Whether an offer is drawn at all (not-working bounces need their filter on). */
export function isOfferVisible(
  facts: OfferFacts,
  filters: OfferFilters,
  catalog: OfferCatalog,
): boolean {
  return catalog.find(facts.reason)?.id !== OFFER_NOT_WORKING_REASON_ID
    ? true
    : Boolean(filters[OFFER_NOT_WORKING_REASON_ID]);
}

/**
 * Marker opacity. With a legend filter on, matching offers are solid and the
 * rest dimmed. Without one, only the "interesting" offers are solid: the
 * accepted/unanswered group and wrong-equipment bounces; every other reason is
 * dimmed so the one that took the order stands out.
 */
export function offerOpacity(
  facts: OfferFacts,
  filters: OfferFilters,
  catalog: OfferCatalog,
): 1 | 0.3 {
  const id = catalog.filterKey(facts.reason);
  if (Object.values(filters).some(Boolean)) return filters[id] ? 1 : 0.3;
  if (id === OFFER_NULL_REASON_ID && facts.accepted) return 1;
  return id === OFFER_WRONG_EQUIPMENT_REASON_ID ? 1 : 0.3;
}

export interface OfferStat {
  /** Reason number as a string (`'0'` = no reason). */
  key: string;
  count: number;
  reason: OfferReason | undefined;
  /** Key into {@link OfferFilters}. */
  filterKey: string;
}

/** One legend entry for a reason key and its count. */
function toStat(key: string, count: number, catalog: OfferCatalog): OfferStat {
  return {
    key,
    count,
    reason: catalog.find(key),
    filterKey: catalog.filterKey(key),
  };
}

/**
 * The legend tallied from the offers themselves (new mode): one entry per
 * reason number (0 = no reason: accepted or pending), ascending.
 */
export function tallyOfferStats(
  records: readonly OfferRecord[] | null | undefined,
  catalog: OfferCatalog,
): OfferStat[] {
  const counts = new Map<number, number>();
  for (const record of records ?? []) {
    const n = record.facts.reason ?? 0;
    counts.set(n, (counts.get(n) ?? 0) + 1);
  }
  return [...counts]
    .sort(([a], [b]) => a - b)
    .map(([n, count]) => toStat(String(n), count, catalog));
}

/**
 * The legend from a server-side `{ "<reason>": count }` tally (legacy mode) —
 * cpanel drew exactly the entries the backend sent, in key order.
 */
export function statsFromCounts(
  counts: Readonly<Record<string, number>> | null | undefined,
  catalog: OfferCatalog,
): OfferStat[] {
  return Object.entries(counts ?? {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([key, count]) => toStat(key, count, catalog));
}

/** Everything the visualization shows for one mode and order. */
export interface OffersView {
  mode: OffersMode;
  records: OfferRecord[];
  stats: OfferStat[];
}

/** Offers that can be drawn, ordered by when they reached the courier. */
export function plotOffers(records: readonly OfferRecord[] | null | undefined): {
  plotted: PlottedOffer[];
  unplottedCount: number;
} {
  const plotted = (records ?? []).filter(
    (record): record is PlottedOffer => record.coords !== null,
  );
  // Oldest offer first; ones without a usable timestamp go last (painted underneath).
  const time = (value: string | null | undefined) => {
    const ms = value ? Date.parse(value) : Number.NaN;
    return Number.isNaN(ms) ? null : ms;
  };
  plotted.sort((a, b) => {
    const ta = time(a.sortAt);
    const tb = time(b.sortAt);
    if (ta === null) return tb === null ? 0 : 1;
    if (tb === null) return -1;
    return ta - tb;
  });
  return { plotted, unplottedCount: (records?.length ?? 0) - plotted.length };
}

/** Great-circle distance in km between two `lat/long` points. */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Straight-line km from the order's pickup to an offer's courier (0 if unknown). */
export function offerDistanceKm(
  origin: LooseLatLong | null | undefined,
  offer: { coords?: LatLong | null | undefined },
): number {
  const lat1 = origin?.lat;
  const lon1 = origin?.long;
  const lat2 = offer.coords?.lat;
  const lon2 = offer.coords?.long;
  if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
  return haversineKm(lat1, lon1, lat2, lon2);
}

/** The km shown inside a marker: the road distance when known, else straight-line. */
export function offerMarkerKm(
  offer: Pick<OfferRecord, 'roadMeters'>,
  straightKm: number,
): number {
  return offer.roadMeters != null ? offer.roadMeters / 1000 : straightKm;
}

const BOUNDS_PADDING = 0.2;
const BOUNDS_MIN_PADDING_DEG = 0.01;

/**
 * Viewport `[[minLng,minLat],[maxLng,maxLat]]` covering the pickup and every
 * plotted courier, padded by 20 % (≈1 km when everything is one point). Null
 * without a pickup, since there is nothing to anchor the picture to.
 */
export function offersBounds(
  origin: LooseLatLong | null | undefined,
  plotted: readonly PlottedOffer[],
): [[number, number], [number, number]] | null {
  if (!origin?.lat || !origin.long) return null;
  const coords: [number, number][] = [
    [origin.long, origin.lat],
    ...plotted.map((offer): [number, number] => [offer.coords.long, offer.coords.lat]),
  ];
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  const [minLng, maxLng] = [Math.min(...lngs), Math.max(...lngs)];
  const [minLat, maxLat] = [Math.min(...lats), Math.max(...lats)];
  const padLng = (maxLng - minLng) * BOUNDS_PADDING || BOUNDS_MIN_PADDING_DEG;
  const padLat = (maxLat - minLat) * BOUNDS_PADDING || BOUNDS_MIN_PADDING_DEG;
  return [
    [minLng - padLng, minLat - padLat],
    [maxLng + padLng, maxLat + padLat],
  ];
}
